import { useState, useCallback } from 'react';
import { DEFAULT_VIEW, type ViewId } from '../constants/viewConfig';
import type { PlaceLayer } from '../types/Place';

// Re-export ViewId as ActiveView for backwards compatibility with components
export type ActiveView = ViewId;

/**
 * useAppState - Centralized state management for the main App component
 *
 * Groups related UI state to reduce the number of useState calls in App.tsx
 * and make state management more organized.
 */
export function useAppState() {
  // View state
  const [activeView, setActiveView] = useState<ActiveView>(DEFAULT_VIEW);
  const [mapReady, setMapReady] = useState(false);

  // Selection/navigation state
  const [selectedPlace, setSelectedPlace] = useState<string | null>(null);
  const [highlightedPlaceId, setHighlightedPlaceId] = useState<string | null>(null);
  const [targetPlaceId, setTargetPlaceId] = useState<string | null>(null);
  const [navigationHighlight, setNavigationHighlight] = useState<string | null>(null);

  // Filter state
  const [activeLayer, setActiveLayer] = useState<'all' | PlaceLayer>('all');

  // Accessibility state
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Gallery state (Story 6.1, 6.4)
  const [galleryIndex, setGalleryIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Journal state (Story 7.3)
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(() => new Set());

  // Map control state (Story 8-5)
  const [resetViewTrigger, setResetViewTrigger] = useState(0);

  // Helper to increment reset view trigger
  const triggerResetView = useCallback(() => {
    setResetViewTrigger(prev => prev + 1);
  }, []);

  // Helper to toggle month collapse state
  const toggleMonthCollapsed = useCallback((monthKey: string) => {
    setCollapsedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  }, []);

  return {
    // View state
    activeView,
    setActiveView,
    mapReady,
    setMapReady,

    // Selection/navigation state
    selectedPlace,
    setSelectedPlace,
    highlightedPlaceId,
    setHighlightedPlaceId,
    targetPlaceId,
    setTargetPlaceId,
    navigationHighlight,
    setNavigationHighlight,

    // Filter state
    activeLayer,
    setActiveLayer,

    // Accessibility state
    statusMessage,
    setStatusMessage,

    // Gallery state
    galleryIndex,
    setGalleryIndex,
    isFullscreen,
    setIsFullscreen,

    // Journal state
    collapsedMonths,
    setCollapsedMonths,
    toggleMonthCollapsed,

    // Map control state
    resetViewTrigger,
    triggerResetView,
  };
}

export type AppState = ReturnType<typeof useAppState>;
